import { useQuery } from '@apollo/client';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, Outlet } from 'react-router';
import { graphql } from '~/apollo/generated/v3';
import { NavLink } from '~/components/common/NavLink';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { Menu } from '~/components/ui/menu';
import { useRouteParam } from '~/hooks/routing';
import {
  geologyWikiPageRoute,
  uploadGeologyDeleteRoute,
  uploadGeologyUpdateDiagramRoute,
  uploadGeologyUpdateRoute,
} from '~/paths';

const UPLOAD_DEPOSITIONAL_WIKI_PAGE = graphql(`
  query UploadDepositionalWikiPage($id: Int) {
    depositionalList(id: $id) {
      id
      title
    }
  }
`);

export default function DepositionalUpdateRoute() {
  const depositionalId = useRouteParam('depositionalId', parseInt);

  const { data, loading } = useQuery(UPLOAD_DEPOSITIONAL_WIKI_PAGE, {
    variables: { id: depositionalId },
  });

  const depositionalWiki = data?.depositionalList.find(
    wiki => wiki.id === depositionalId,
  );

  useBreadcrumb(
    'routes/upload/depositional/$depositionalId',
    depositionalWiki?.title ?? 'Update Geology Wiki',
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!depositionalWiki) return <NotFound />;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <PageHeading>{depositionalWiki.title}</PageHeading>
        <Link
          className="btn btn-primary btn-sm"
          to={geologyWikiPageRoute(depositionalWiki.id)}
        >
          Wiki Page <FontAwesomeIcon icon={faExternalLink} />
        </Link>
      </div>

      <div className="grid lg:grid-cols-6 gap-4">
        <Menu>
          <Menu.Item>
            <NavLink to={uploadGeologyUpdateRoute(depositionalWiki.id)} end>
              Overview
            </NavLink>
          </Menu.Item>
          <Menu.Item>
            <NavLink to={uploadGeologyUpdateDiagramRoute(depositionalWiki.id)}>
              Diagram
            </NavLink>
          </Menu.Item>
          <Menu.Item>
            <NavLink to={uploadGeologyDeleteRoute(depositionalWiki.id)}>
              Delete Wiki Page
            </NavLink>
          </Menu.Item>
        </Menu>

        <div className="lg:col-span-5">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
